import { Button, Center, Flex, Modal, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { IconFigmaArrowLeft, IconFigmaCheck } from '@/assets/icons';

interface SubmitSuccessModalProps {
  opened: boolean;
  weekNumber: number;
  period: string;
  recipient: string;
  onClose: () => void;
  variant?: 'submit' | 'correction';
}

export const SubmitSuccessModal = ({
  opened,
  weekNumber,
  period,
  recipient,
  onClose,
  variant = 'submit'
}: SubmitSuccessModalProps) => {
  const { t } = useTranslation();
  const isCorrection = variant === 'correction';

  const details: { label: string; value: string; tone?: 'success' }[] = [
    {
      label: t('weekReportDetail.submitSuccessStatus'),
      value: t(
        isCorrection
          ? 'weekReportCorrection.resubmitSuccessStatusValue'
          : 'weekReportDetail.submitSuccessStatusValue'
      ),
      tone: 'success'
    },
    { label: t('weekReportDetail.submitSuccessTo'), value: recipient },
    {
      label: t('weekReportDetail.submitSuccessNextStep'),
      value: t(
        isCorrection
          ? 'weekReportCorrection.resubmitSuccessNextStepValue'
          : 'weekReportDetail.submitSuccessNextStepValue'
      )
    }
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      withCloseButton={false}
      size={560}
      overlayProps={{ backgroundOpacity: 0.55 }}
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 0 }
      }}
    >
      <Stack align="center" gap={24} p={{ base: 20, sm: 56 }}>
        <Center
          w={{ base: 52, sm: 64 }}
          h={{ base: 52, sm: 64 }}
          style={{
            borderRadius: 999,
            backgroundColor: 'var(--color-status-success-soft)',
            color: 'var(--color-status-success)'
          }}
        >
          <IconFigmaCheck size={28} />
        </Center>

        <Stack align="center" gap={12}>
          <Title
            order={3}
            fz={{ base: 19, sm: 24 }}
            fw={700}
            ta="center"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t(
              isCorrection
                ? 'weekReportCorrection.resubmitSuccessTitle'
                : 'weekReportDetail.submitSuccessTitle'
            )}
          </Title>
          <Text
            fz={{ base: 13.5, sm: 15 }}
            ta="center"
            lh={{ base: '20px', sm: '24px' }}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t(
              isCorrection
                ? 'weekReportCorrection.resubmitSuccessMessage'
                : 'weekReportDetail.submitSuccessMessage',
              { week: weekNumber, period }
            )}
          </Text>
        </Stack>

        <Flex
          direction={{ base: 'column', sm: 'row' }}
          gap={{ base: 10, sm: 24 }}
          p={{ base: 14, sm: 24 }}
          w="100%"
          style={{
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-canvas)'
          }}
        >
          {details.map((detail) => (
            <Flex
              key={detail.label}
              direction={{ base: 'row', sm: 'column' }}
              justify={{ base: 'space-between', sm: 'flex-start' }}
              align="center"
              gap={{ base: 12, sm: 6 }}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Text
                fz={{ base: 10, sm: 11 }}
                fw={600}
                ta={{ base: 'left', sm: 'center' }}
                style={{ color: 'var(--color-text-muted)' }}
              >
                {detail.label}
              </Text>
              <Text
                fz={{ base: 13, sm: 15 }}
                fw={700}
                ta={{ base: 'right', sm: 'center' }}
                style={{
                  color:
                    detail.tone === 'success'
                      ? 'var(--color-status-success)'
                      : 'var(--color-text-primary)'
                }}
              >
                {detail.value}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Button fullWidth leftSection={<IconFigmaArrowLeft size={20} />} onClick={onClose}>
          {t('weekReportDetail.submitSuccessBack')}
        </Button>
      </Stack>
    </Modal>
  );
};
